import { withContent } from 'components/Content';
import React, { Fragment, useState } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'recompose';
import classNames from 'classnames';
import { get, filter } from 'lodash';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import { withStyles } from '@material-ui/core';
import { Div } from 'components/Ui';
import { Mobile, Default } from 'components/Media';
import Link from 'components/ContentfulFields/Link';
import { getImage } from 'images';
import {
  getRegionalBrandName,
  ALL_BRANDS,
  US_BRANDS,
  CA_BRANDS,
} from 'utils/domainHelper';
import {
  injectVariables,
  translateData,
  getPrimoBrandName,
} from 'utils/translation';

import {
  selectAcquisitionVariables,
  selectSsrReqRoute,
} from 'containers/Landing/selectors';
import styles from './waterTypes.scss';
import jsStyles from './styles';
import { BackPlay, ForwardPlay } from 'images';
import ContentfulFields from 'components/ContentfulFields';

const CONTENT_KEYS = {
  title: 'Title',
  features: 'Water Types',
};

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const WaterTypes = ({ content, theme, ssrReqRoute, acquisitionVariables }) => {
  const [activeStep, setActiveStep] = useState(0);

  const {
    title,
    features: {
      fields: { features: _features },
    },
  } = content;

  const handleStepChange = (value) => {
    setActiveStep(value);
  };

  const handleImageClick = (event) => {
    const nextStep = parseInt(
      event.currentTarget.attributes.slideindex.value,
      10,
    );

    setActiveStep(nextStep);
  };

  const handleArrowKeyPress = (event, value, max) => {
    const keyCode = event.keyCode
      ? event.keyCode
      : event.which
      ? event.which
      : event.charCode;
    // eslint-disable-next-line eqeqeq
    if (keyCode == 13 || keyCode == 32) {
      handleArrowClick(value, max);
    }
  };

  const handleArrowClick = (value, max) => {
    const newValue = activeStep + value;
    if (newValue < 0 || newValue > max) {
      return;
    }

    setActiveStep((prev) => prev + value);
  };

  const isSupportedFeature = (feature) => {
    let supportedBrands = get(feature, 'fields.supportedBrands', 'All Brands');

    switch (supportedBrands) {
      case 'US Brands':
        supportedBrands = US_BRANDS;
        break;
      case 'CA Brands':
        supportedBrands = CA_BRANDS;
        break;
      case 'All Brands':
        supportedBrands = ALL_BRANDS;
        break;
    }

    const brandName = getPrimoBrandName();
    return supportedBrands.includes(brandName);
  };

  const composeFeature = (feature, index, clickable = false) => {
    const currentStep = index === activeStep;
    const imageSrc = getImage(feature, 'fields.images[0].fields.file.url');
    const imageSrcInactive = getImage(
      feature,
      'fields.images[1].fields.file.url',
    );

    // TODO: aria-label
    const carouselLabel1 = translateData('Click To Move Carousel To', 'C1.1');
    const carouselLabel2 = translateData('Content', 'C1.2');
    const hasInactiveImage = feature.fields.images[1] && imageSrcInactive;
    return (
      <Div
        slideindex={index}
        onClick={clickable && handleImageClick}
        onKeyPress={clickable && handleImageClick}
        className={classNames(styles.item, { [styles.active]: currentStep })}
        key={get(feature, 'sys.id')}
        role="tab"
        aria-label={`${carouselLabel1} ${feature.fields.title} ${carouselLabel2}`}
        tabIndex={0}
        aria-selected={currentStep}
        aria-controls="tab-content"
      >
        <img
          src={`${
            currentStep
              ? imageSrc
              : hasInactiveImage
              ? imageSrcInactive
              : imageSrc
          }?fm=webp`}
          alt={
            feature.fields.images[0].fields.file.title ||
            `${feature.fields.title} Image`
          }
        />
        <span>{translateData(feature.fields.title, 'C1')}</span>
      </Div>
    );
  };

  const regionalBrandName = getRegionalBrandName(ssrReqRoute, 12);

  const carouselItemOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <div className={styles.description} role="tabpanel">
          {injectVariables(
            children,
            regionalBrandName,
            acquisitionVariables,
            'C18',
          )}
        </div>
      ),
      [INLINES.HYPERLINK]: (node, children) => {
        const uri = get(node, 'data.uri', '/');
        return (
          <Link to={uri}>
            {injectVariables(
              children,
              regionalBrandName,
              acquisitionVariables,
              'C19',
            )}
          </Link>
        );
      },
      [BLOCKS.HEADING_6]: (node, children) => (
        <h6 className={styles.subtext}>
          {injectVariables(
            children,
            regionalBrandName,
            acquisitionVariables,
            'C20',
          )}
        </h6>
      ),
    },
  };

  const features = filter(_features, isSupportedFeature);

  return (
    <section className={styles.waterTypes}>
      <div className={styles.carouselWrapper}>
        <div className={styles.carousel} role="tablist">
          <h1 className={styles.title}>
            {injectVariables(
              title,
              regionalBrandName,
              acquisitionVariables,
              'C21',
            )}
          </h1>
          <Default>
            <div className={styles.images}>
              {features.map((feature, index) =>
                composeFeature(feature, index, true),
              )}
            </div>
          </Default>

          <AutoPlaySwipeableViews
            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
            index={activeStep}
            onChangeIndex={handleStepChange}
            className={styles.swipeable}
            enableMouseEvents
            interval={2000000}
          >
            {features.map((feature, index) => (
              <Fragment key={get(feature, 'sys.id')}>
                <Mobile>{composeFeature(feature, index)}</Mobile>
                <div className={styles.carouselContent}>
                  <img
                    src={`${feature.fields.carouselImage?.fields.file.url}?fm=webp`}
                    alt={feature.fields.title || ''}
                    aria-hidden="true"
                  />
                  <div className={styles.swipeableFeature} id="tab-content">
                    <h1 className={styles.title}>{feature.fields.title}</h1>
                    {documentToReactComponents(
                      feature.fields.description,
                      carouselItemOptions,
                    )}
                    {feature.fields.carouselButton && (
                      <ContentfulFields
                        type={get(
                          feature.fields.carouselButton,
                          'sys.contentType.sys.id',
                        )}
                        document={feature.fields.carouselButton}
                        key={feature.fields.carouselButton.sys.id}
                      />
                    )}
                  </div>
                </div>
              </Fragment>
            ))}
          </AutoPlaySwipeableViews>
          <Mobile>
            <Div
              className={classNames(styles.backPlay, {
                [styles.disabled]: activeStep === 0,
              })}
              role="button"
              aria-disabled={activeStep === 0}
              aria-label="Go to previous carousel panel"
              onClick={() => handleArrowClick(-1, features.length - 1)}
              onKeyPress={(event) =>
                handleArrowKeyPress(event, -1, features.length - 1)
              }
              tabIndex="0"
            >
              <img src={BackPlay} alt="Back" />
            </Div>
            <Div
              className={classNames(styles.forwardPlay, {
                [styles.disabled]: activeStep === features.length - 1,
              })}
              role="button"
              aria-disabled={activeStep === features.length - 1}
              aria-label="Go to next carousel panel"
              onClick={() => handleArrowClick(1, features.length - 1)}
              onKeyPress={(event) =>
                handleArrowKeyPress(event, 1, features.length - 1)
              }
              tabIndex="0"
            >
              <img src={ForwardPlay} alt="Forward" />
            </Div>
          </Mobile>
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = createStructuredSelector({
  acquisitionVariables: selectAcquisitionVariables(),
  ssrReqRoute: selectSsrReqRoute(),
});
const mapDispatchToProps = {};

export default compose(
  withStyles(jsStyles, { withTheme: true }),
  connect(mapStateToProps, mapDispatchToProps),
  withContent(CONTENT_KEYS),
)(WaterTypes);
